import React, { useEffect, useRef } from 'react';
import './App.scss';
import './brand.scss';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import 'url-search-params-polyfill';
import { useDispatch } from 'react-redux';

import config from 'config';
import device from 'common/support/device';

import { clearVideoIsClicked } from 'actions/clickedVideo';

import AppActionsHandler from 'components/support/AppActionsHandler';
import LoggerOverlay from 'components/support/LoggerOverlay';
import AuthProvider from 'components/providers/AuthProvider';
import { FeatureFlagProvider } from 'components/providers/FeatureFlagProvider';
import AriaTextToSpeech from 'components/support/AriaTextToSpeech';

import { useNextVideo, usePrevVideo, useFallbackVideo } from 'hooks/video';
import { useVegaRealFocusSync } from 'hooks/focus';

import textToSpeechManager from 'support/tts/TextToSpeechManager';

import PrivacyManager from 'support/privacy/PrivacyManager';

import BrandSplashImage from 'components/BrandSplashImage';
import RouteLoadingFallback from 'components/common/RouteLoadingFallback';
import { PlayerStateProvider } from 'components/providers/PlayerStateContext';
import ExitDialog from 'components/pages/ExitDialog';
import { FormattedMessage } from 'react-intl';
import KitchenSinkFetcher from 'components/KitchenSinkFetcher';
import FMSFetcher from 'components/FMSFetcher';
import IdleModeOverlay from 'components/IdleModeOverlay';
import { useIdleModeTimer } from 'hooks/useIdleModeTimer';
import { MemoryHistory } from 'history';

// SplashWrapper itself pulls in a large module graph (Redux hooks,
// IntlProvider, ErrorModal, device, platform/deeplink/closedCaption hooks,
// etc.), so it's also code-split. This means the very first paint only needs
// BrandSplashImage + config, not any of that.
const SplashWrapper = React.lazy(() => import('components/SplashWrapper'));

// Route/page-level components are code-split via React.lazy so App can mount
// (and SplashWrapper/BrandSplashImage can paint) without waiting for every
// route's module graph to download first. Each is only fetched once its
// route is actually navigated to.
const Home = React.lazy(() => import('components/pages/home/Home'));
const UpcomingEventPage = React.lazy(() => import('components/pages/UpcomingEvent'));
const UpgradeToWatch = React.lazy(() => import('components/pages/playback/UpgradeToWatch'));
const ShortCode = React.lazy(() => import('components/pages/shortCode/ShortCode'));
const Logout = React.lazy(() => import('components/pages/Logout'));
const PlaybackPage = React.lazy(() => import('components/playback/PlaybackPage'));
const LegalNoticesPage = React.lazy(() => import('components/pages/legalNotices/LegalNotices'));
const ClosedCaptionsPage = React.lazy(() => import('components/pages/closedCaptions/ClosedCaptions'));
const ErrorPage = React.lazy(() => import('components/pages/Error'));
const SyncbakErrorPage = React.lazy(() => import('components/error/SyncbakError'));
const NetworkErrorPage = React.lazy(() => import('components/error/NetworkError'));
const ContactUsPage = React.lazy(() => import('components/pages/contactUs/ContactUs'));
const InteriorPage = React.lazy(() => import('components/pages/home/InteriorPage'));
const SettingsPage = React.lazy(() => import('components/pages/home/SettingsPage'));
const LandingPage = React.lazy(() => import('components/pages/landing/LandingPage'));
const PrivacyPageUS = React.lazy(() => import('components/pages/privacy/PrivacyPageUS'));
const PrivacyPageGDPR = React.lazy(() => import('components/pages/privacy/PrivacyPageGDPR'));
const GraphQLPocRoutes = React.lazy(() => import('components/pages/graphqlPoc/GraphQLPocRoutes'));

const App = () => {
  const history = useHistory() as MemoryHistory;
  const dispatch = useDispatch();
  PrivacyManager.initializeCMP();
  useVegaRealFocusSync();
  useIdleModeTimer();

  useEffect(() => {
    const {
      brand: { name },
    } = config;
    if (document) {
      document.title = name;
    }
  }, []);

  useEffect(() => {
    const stopTTS = () => {
      textToSpeechManager.stop();
    };
    // initiate the event handler
    window.addEventListener('beforeunload', stopTTS);

    // this will clean up the event every time the component is re-rendered
    return () => {
      window.removeEventListener('beforeunload', stopTTS);
    };
  }, []);

  useEffect(() => {
    if (!device.isVega) return undefined;

    const methodNames = ['push', 'replace', 'goBack', 'go'] as const;
    const originals = methodNames.map(name => history[name]);

    methodNames.forEach((name, index) => {
      (history[name] as unknown) = (...args: unknown[]) => {
        textToSpeechManager.stop();
        return (originals[index] as (...a: unknown[]) => unknown).apply(
          history,
          args,
        );
      };
    });

    return () => {
      methodNames.forEach((name, index) => {
        (history[name] as unknown) = originals[index];
      });
    };
  }, [history]);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const videoParam = params.get('video') ?? undefined;
  const rowParam = params.get('row') ?? undefined;
  const isDiscoveryRowVideo = params.get('isDiscoveryRowVideo') === 'true';
  const autoplay = params.get('autoplay') === 'true';
  const [prevVideo, prevRow] = usePrevVideo();
  const [nextVideo, nextRow] = useNextVideo();
  const [fallbackVideo] = useFallbackVideo();

  const onPrevVideo = () => {
    if (prevVideo && prevRow) {
      const searchParam = new URLSearchParams(location.search);
      searchParam.set('video', prevVideo.id);
      searchParam.set('row', prevRow.id);

      history.replace({
        pathname: history.location.pathname,
        search: searchParam.toString(),
      });
    }
  };

  const onNextVideo = (isAutoplay = false) => {
    if (nextVideo) {
      const searchParam = new URLSearchParams(location.search);
      searchParam.set('video', nextVideo.id);
      searchParam.set('row', nextRow?.id as unknown as string);

      if (isAutoplay) {
        searchParam.set('autoplay', isAutoplay as unknown as string);
      }

      history.replace({
        pathname: history.location.pathname,
        search: searchParam.toString(),
      });
    }
  };

  const onVideoCompleted = () => {
    dispatch(clearVideoIsClicked());
    onNextVideo(true);
  };

  const handleError = (err: unknown) => {
    const error = err as { type: string; providers?: string[]; isCarousel?: boolean; code?: string };
    let path;
    let redirect;
    switch (error.type) {
      case 'authN':
        path = {
          pathname: '/shortCode',
          search: `?providers=${error.providers?.join(',')}`,
        };
        redirect = {
          redirectTo: location,
        };
        if (error.isCarousel) {
          history.push(path, redirect);
          break;
        }

        history.replace(path, redirect);
        break;
      case 'upcoming':
        path = {
          pathname: `/upcoming/${videoParam}`,
        };
        redirect = {
          redirectTo: location,
        };

        if (error.isCarousel) {
          history.push(path, redirect);
          break;
        }

        history.replace(path, redirect);
        break;
      case 'authZ': {
        history.replace({
          pathname: '/error/authz',
        });
        break;
      }
      case 'DRM':
      case 'playback': {
        history.replace({
          pathname: '/error/playbackStartup',
        });
        break;
      }
      case 'syncbak': {
        history.replace({
          pathname: '/error/syncbak',
          search: `?code=${error.code}`,
        });
        break;
      }
      case 'network': {
        history.push({
          pathname: '/error/network',
        });
        break;
      }
      default:
        break;
    }
  };

  const ksFetcherRef = useRef<{ retry: () => Promise<unknown> }>(null);

  return (
    <AppActionsHandler>
      <LoggerOverlay>
        <AriaTextToSpeech />
        <KitchenSinkFetcher
          onKSStartupError={() => {
            history.replace({
              pathname: '/error/connection',
            });
          }}
          ref={ksFetcherRef}
        />
        <FeatureFlagProvider>
          <React.Suspense fallback={<BrandSplashImage />}>
          <SplashWrapper>
            <div className={classNames('h-100', 'w-100', 'app-content', 'text-white')}>
              <React.Suspense fallback={<RouteLoadingFallback />}>
              <Switch>
                <PlayerStateProvider>
                  <Route path="/">
                    <AuthProvider>
                      <Switch>
                        <Route path="/error/authz">
                          <ErrorPage message={<FormattedMessage id="error.message.authZ" />} />
                        </Route>
                        <Route path="/error/network">
                          <NetworkErrorPage />
                        </Route>
                        <Route path="/error/syncbak">
                          <SyncbakErrorPage />
                        </Route>
                        <Route path="/error/playbackStartup">
                          <ErrorPage
                            message={<FormattedMessage id="error.message.playbackStartup" />}
                            onDismiss={() => {
                              const searchParam = new URLSearchParams();
                              searchParam.set('video', fallbackVideo!.id as unknown as string);
                              history.replace({
                                pathname: '/',
                                search: searchParam.toString(),
                              });
                            }}
                          />
                        </Route>
                        <Route path="/error">
                          <ErrorPage message={<FormattedMessage id="error.message.default" />} />
                        </Route>
                        <Route path="/upcoming/:contentId">
                          <UpcomingEventPage />
                        </Route>
                        <Route path="/upgradeToWatch">
                          <UpgradeToWatch videoId={videoParam ?? undefined} rowId={rowParam ?? undefined} />
                        </Route>
                        <Route path="/shortCode">
                          <ShortCode />
                        </Route>
                        <Route path="/logout">
                          <Logout />
                        </Route>
                        <Route path="/legal">
                          <LegalNoticesPage />
                        </Route>
                        <Route path="/privacyUS">
                          <PrivacyPageUS />
                        </Route>
                        <Route path="/privacyGDPR">
                          <PrivacyPageGDPR />
                        </Route>
                        <Route path="/closedCaptions">
                          <ClosedCaptionsPage />
                        </Route>
                        <Route path="/contactUs">
                          <ContactUsPage />
                        </Route>
                        <Route path="/settings">
                          <SettingsPage pageName="Settings" pageType="custom" />
                        </Route>
                        <Route path="/sport/:contentId">
                          <InteriorPage pageName="sports" pageType="sport" />
                        </Route>
                        <Route path="/league/:contentId">
                          <InteriorPage pageName="leagues" pageType="league" />
                        </Route>
                        <Route path="/show/:contentId">
                          <InteriorPage pageName="shows" pageType="show" />
                        </Route>
                        <Route path="/market/:contentId">
                          <InteriorPage pageName="markets" pageType="market" />
                        </Route>
                        <Route path="/tagrow/:contentId">
                          <LandingPage pageType="tagrow" />
                        </Route>
                        <Route path="/graphql-poc">
                          <GraphQLPocRoutes />
                        </Route>
                        <Route path="/playback">
                          <PlaybackPage
                            videoId={videoParam}
                            rowId={rowParam}
                            isAutoplay={autoplay}
                            onVideoCompleted={onVideoCompleted}
                            onError={handleError}
                            onPrevVideo={onPrevVideo}
                            onNextVideo={onNextVideo}
                            isDiscoveryRowVideo={isDiscoveryRowVideo}
                          />
                        </Route>
                        <Route path="/">
                          <Home onError={handleError} />
                        </Route>
                      </Switch>
                    </AuthProvider>
                  </Route>
                  <Route path="/error/connection">
                    <ErrorPage
                      message={<FormattedMessage id="error.message.connection" />}
                      onDismiss={async () => {
                        const results = await ksFetcherRef.current?.retry();
                        if (results) {
                          history.replace({ pathname: '/' });
                        }
                      }}
                    />
                  </Route>
                </PlayerStateProvider>
              </Switch>
              </React.Suspense>
            </div>
            <ExitDialog />
            <FMSFetcher />
            <IdleModeOverlay />
          </SplashWrapper>
          </React.Suspense>
        </FeatureFlagProvider>
      </LoggerOverlay>
    </AppActionsHandler>
  );
};

export default App;
